// Which steps a replay request actually runs.
//
// Pure: no database handle, no clock, no telemetry. It takes the request, the
// member's ledger rows and the tier, and returns an ordered step set plus a
// count for every step it declined. That shape is deliberate — a repair tool
// that silently narrows what it was asked to do is worse than one that refuses,
// so every drop is counted and the caller meters it.
//
// One resolver serves both tiers AND the operator CLI's dry run. It lives here
// rather than beside a runner so the CLI can import the function the consumers
// run instead of restating it: a restated copy is a second definition of "what
// a replay would do", and the preview it drives can then disagree with the
// consumer it previews. The tier's step vocabulary comes from
// `manifestStepsForTier` and the gap classes from `classifyStepReason`, so the
// CLI, this resolver and the gap report cannot disagree about what a gap is.
// If this ever needs a per-tier branch, that is a signal the resolver is wrong
// rather than that a tier is special.
//
// The inputs are structural on purpose. The consumers hand it Drizzle rows and
// the CLI its own read model; both carry these three fields, and the reason is
// a plain string because the replay-reason vocabulary belongs to the events
// package, which depends on this one rather than the other way round.

import {
  dependencyUpstreamOf,
  isUpstreamRowUsable,
  type UpstreamStepName,
} from './dependency-refusal';
import type { MemberMigrationTier } from './schema';
import { classifyStepReason, manifestStepsForTier, stepDetailCode } from './step-manifest';

/** The reason that unlocks a `human`-class step: a person at a terminal taking responsibility. */
const UNLOCKING_REASON = 'operator_manual';

/** The ledger fields the resolver reads, whatever query produced them. */
export interface ReplayLedgerRowInput {
  readonly stepName: string;
  readonly status: string;
  readonly error: string | null;
}

export interface ResolveReplayStepsInput {
  tier: MemberMigrationTier;
  /** The request's explicit list, or undefined for "every gap this tier has". */
  steps?: readonly string[] | undefined;
  /** The request's replay reason code. */
  reason: string;
  /**
   * Every ledger row this member holds for this tier, and may also carry the
   * member's rows of other tiers.
   *
   * A row of another tier is read only as the upstream of one of this tier's
   * dependents (a Tier 1 step waiting on `tier0.program`): it never joins the
   * resolved set and is never counted. Without it such a dependent stays
   * human-locked, because nothing says its upstream was repaired. So a caller
   * that resolves a dependent of an earlier tier passes the member's whole
   * ledger.
   */
  ledger: readonly ReplayLedgerRowInput[];
  /**
   * The steps the CALLING runner can actually execute, when that is narrower
   * than the tier.
   *
   * A tier's manifest is the gap vocabulary, not one runner's repertoire: a
   * tier 1 gap may belong to a different consumer entirely — `tier1.sona_context`
   * is owned by the sona-context runner, not the Tier 1 transfer — so resolving
   * against the manifest alone hands a runner a step it has no body for. The
   * runner's own filter would then drop it in silence, and the operator would
   * see a successful replay that ran nothing.
   *
   * Omitted means "this caller owns the whole tier": it reports the gap,
   * whoever has to run it.
   */
  runnable?: readonly string[] | undefined;
}

// A type alias rather than an interface: callers pass the counts to telemetry as a
// string-keyed record, and only an alias is assignable to an index signature.
export type ResolveReplayStepsCounts = {
  /** Named by the caller but not a step of this tier. Never run. */
  dropped_not_in_manifest: number;
  /**
   * A genuine gap of this tier that this runner does not own.
   *
   * Counted rather than dropped quietly: it is the one class the operator
   * cannot act on by re-running, so a replay that resolves to nothing BUT
   * this needs to say so.
   */
  dropped_not_runnable: number;
  /** A `human`-class step that was not unlocked. See the rule below. */
  dropped_human_class: number;
  /** A `running` row left out of the DEFAULT set. See the rule below. */
  dropped_running: number;
  /**
   * A `done` row left out of the default set.
   *
   * Only the default set: a `done` step NAMED explicitly is passed through to
   * `beginStep`, which short-circuits it. The no-op is then proven by the
   * ledger rather than by this function's arithmetic, which is the whole
   * point — a caller must not be able to talk the runner into re-running a
   * step that succeeded, and this resolver must not be the thing trusted to
   * prevent it.
   */
  dropped_already_done: number;
  /**
   * A `skipped` row whose reason classifies `settled`, NAMED by a request
   * without the unlocking reason.
   *
   * The default set never includes one (a settled skip leaves no work
   * outstanding), so this counts only named drops. A settled skip is a step
   * that correctly found nothing to do, or one a person closed on purpose; a
   * cohort selector or sweep that names it must not quietly re-open it. Only
   * `operator_manual` does.
   */
  dropped_settled: number;
};

export interface ResolveReplayStepsResult {
  /** The steps to run, in manifest order. */
  steps: readonly string[];
  counts: ResolveReplayStepsCounts;
}

/**
 * Whether a ledger row leaves something outstanding.
 *
 * The classes are the gap report's own, read through `classifyStepReason` so
 * there is one definition. `skipped` is the interesting case: a `settled` skip
 * is a step that correctly found nothing to do and never becomes a gap, while
 * `retryable` and `human` skips both leave work outstanding.
 *
 * `human` is deliberately admitted HERE and refused later, by the single rule
 * below, rather than being filtered out at this point. Dropping it here would
 * be invisible: it would leave `dropped_human_class` at zero, so a sweep that
 * asked for a member and got nothing back could not tell "this member is clean"
 * from "this member is waiting on a person". That count is the signal.
 */
function leavesWorkOutstanding(
  row: Pick<ReplayLedgerRowInput, 'status' | 'error'> | undefined,
): boolean {
  if (row === undefined) return true;
  if (row.status === 'failed' || row.status === 'pending') return true;
  if (row.status === 'skipped') return classifyStepReason(row.error) !== 'settled';
  return false;
}

/**
 * Whether a row's recorded reason is one the human rule reads.
 *
 * Both settled outcomes carry one. A `skipped` row's reason is the decision the
 * step took; a `failed` row's is the fault that stopped it, and several faults
 * are themselves a person's job — a 1.0 row that cannot be parsed, a Healthie
 * id another member already holds, a source that refused the read. Re-running
 * one of those re-settles it identically, so leaving `failed` outside the rule
 * would have every replay, and any scheduled sweep, re-request it forever.
 * `pending` and `running` rows have recorded no outcome yet, so they have no
 * reason to read.
 */
function carriesSettledReason(row: Pick<ReplayLedgerRowInput, 'status'>): boolean {
  return row.status === 'skipped' || row.status === 'failed';
}

/**
 * The upstream a `failed` row is waiting on, when it refused with
 * `dependency_unrecoverable`. Undefined for any other row, and for a step the
 * dependency register does not name.
 *
 * The upstream comes from the register, not from the reason's qualifier. The
 * qualifier is for a person reading the ledger, and a row written before every
 * producer recorded one carries none; the register is the declaration the
 * runners check against, so reading it here keeps the unlock and the runner on
 * one definition.
 */
function waitingOn(
  step: string,
  row: ReplayLedgerRowInput | undefined,
): UpstreamStepName | undefined {
  if (row?.status !== 'failed' || row.error === null) return undefined;
  if (stepDetailCode(row.error) !== 'dependency_unrecoverable') return undefined;
  return dependencyUpstreamOf(step);
}

export function resolveReplaySteps(input: ResolveReplayStepsInput): ResolveReplayStepsResult {
  const manifest = manifestStepsForTier(input.tier);
  const rows = new Map(input.ledger.map((row) => [row.stepName, row]));
  const counts: ResolveReplayStepsCounts = {
    dropped_not_in_manifest: 0,
    dropped_not_runnable: 0,
    dropped_human_class: 0,
    dropped_running: 0,
    dropped_already_done: 0,
    dropped_settled: 0,
  };

  // An explicit list is intersected with the manifest, never iterated: order is
  // the manifest's property, and a name outside it is dropped and counted
  // rather than passed to a runner that has no body for it.
  let candidates: readonly string[];
  if (input.steps === undefined) {
    candidates = manifest.filter((step) => {
      const row = rows.get(step);
      if (row?.status === 'done') {
        counts.dropped_already_done += 1;
        return false;
      }
      // A `running` row is indistinguishable from a delivery still in flight —
      // the ledger records `started_at` and `attempts` but holds no lease — so
      // including it by default risks two concurrent runs of the same step.
      // It is reachable only by being named.
      if (row?.status === 'running') {
        counts.dropped_running += 1;
        return false;
      }
      return leavesWorkOutstanding(row);
    });
  } else {
    const named = new Set(input.steps);
    counts.dropped_not_in_manifest = [...named].filter((step) => !manifest.includes(step)).length;
    candidates = manifest.filter((step) => named.has(step));
  }

  // The `human` rule, applied last so it governs both paths. A step whose
  // recorded reason classifies `human` runs only when it was NAMED and the
  // request's reason is `operator_manual` — a person at a terminal taking
  // responsibility. Any other reason, `reconciler_sweep` above all, drops it.
  // This is enforced here, at the consumer, rather than trusted to a future
  // scheduled caller.
  const unlocked = input.steps !== undefined && input.reason === UNLOCKING_REASON;
  const owned =
    input.runnable === undefined
      ? candidates
      : candidates.filter((step) => {
          if (input.runnable?.includes(step)) return true;
          counts.dropped_not_runnable += 1;
          return false;
        });
  const droppedHuman = new Set<string>();
  const steps = owned.filter((step) => {
    const row = rows.get(step);
    if (row === undefined || !carriesSettledReason(row)) return true;
    const reasonClass = classifyStepReason(row.error);
    // A settled skip can only be here because it was named: the default set
    // drops it as leaving nothing outstanding. Named, it is re-opened only by
    // a person taking responsibility, exactly like a human-class row.
    if (row.status === 'skipped' && reasonClass === 'settled') {
      if (unlocked) return true;
      counts.dropped_settled += 1;
      return false;
    }
    if (reasonClass !== 'human') return true;
    if (unlocked && input.steps?.includes(step)) return true;
    // A dependent refused only because its upstream was waiting on a person.
    // Once that upstream is usable, by the same rule the runner applies,
    // nothing is waiting any more, so the dependent heals like a retryable
    // failure under any reason. The upstream may be a step of an earlier tier,
    // read from the rows the caller passed beside this tier's.
    const upstream = waitingOn(step, row);
    if (upstream !== undefined && isUpstreamRowUsable(upstream, rows.get(upstream))) return true;
    counts.dropped_human_class += 1;
    droppedHuman.add(step);
    return false;
  });

  // A run that re-runs an upstream also re-runs the dependents it left
  // refusing, so they see its fresh outcome in the same delivery: the runner
  // orders the upstream first. They are added only when their own upstream is
  // in the run, never from one still failed and not re-run, so a refusal can
  // not loop. A dependent an operator settled is `skipped`, not `failed`, and
  // is never re-opened here.
  const running = new Set(steps);
  const dependents = manifest.filter((step) => {
    if (running.has(step)) return false;
    const upstream = waitingOn(step, rows.get(step));
    if (upstream === undefined || !running.has(upstream)) return false;
    return input.runnable === undefined || input.runnable.includes(step);
  });
  if (dependents.length === 0) return { steps, counts };
  // A dependent dropped above and added back here runs, so it is not counted
  // as dropped.
  for (const step of dependents) {
    if (droppedHuman.has(step)) counts.dropped_human_class -= 1;
  }
  const withDependents = new Set([...steps, ...dependents]);
  return { steps: manifest.filter((step) => withDependents.has(step)), counts };
}
